import React from 'react';
import { Query, Mutation } from 'react-apollo';
import gql from 'graphql-tag';
import { CircularProgress, Box } from '@material-ui/core';
import SequenceBuilder from '../../components/EmailPlatform/SequenceBuilder';
import { withSnackbar } from 'notistack';
import { withRouter } from 'react-router-dom';

const GET_SEQUENCE = gql`
  query GetEmailSequence($id: ID!) {
    emailSequence(id: $id) {
      id
      name
      description
      category
      active
      sequence_tag_list
      steps {
        id
        position
        delayDays
        delayHours
        delayMinutes
        emailTemplate {
          id
          name
        }
      }
    }
  }
`;

const GET_TEMPLATES = gql`
  query GetEmailTemplates {
    emailTemplates {
      id
      name
      category
    }
  }
`;

const CREATE_SEQUENCE = gql`
  mutation CreateEmailSequence(
    $name: String!
    $description: String
    $category: String
    $active: Boolean
    $steps: [EmailSequenceStepInput!]
    $sequenceTagList: [String!]
  ) {
    create_email_sequence(
      name: $name
      description: $description
      category: $category
      active: $active
      steps: $steps
      sequenceTagList: $sequenceTagList
    ) {
      emailSequence {
        id
        name
        description
        category
        active
        sequence_tag_list
        steps {
          id
          position
          delayDays
          delayHours
          delayMinutes
          emailTemplate {
            id
            name
          }
        }
      }
      errors
    }
  }
`;

const UPDATE_SEQUENCE = gql`
  mutation UpdateEmailSequence(
    $id: ID!
    $name: String
    $description: String
    $category: String
    $active: Boolean
    $steps: [EmailSequenceStepInput!]
    $sequenceTagList: [String!]
  ) {
    update_email_sequence(
      id: $id
      name: $name
      description: $description
      category: $category
      active: $active
      steps: $steps
      sequenceTagList: $sequenceTagList
    ) {
      emailSequence {
        id
        name
        description
        category
        active
        sequence_tag_list
        steps {
          id
          position
          delayDays
          delayHours
          delayMinutes
          emailTemplate {
            id
            name
          }
        }
      }
      errors
    }
  }
`;

class SequenceBuilderContainer extends React.Component {
  handleSave = (createSequence, updateSequence) => (sequenceData) => {
    const mutation = sequenceData.id ? updateSequence : createSequence;
    const variables = {
      name: sequenceData.name,
      description: sequenceData.description,
      category: sequenceData.category,
      active: sequenceData.active,
      sequenceTagList: sequenceData.sequenceTagList || [],
      steps: sequenceData.steps.map(step => ({
        emailTemplateId: step.templateId,
        delayDays: step.delayDays || 0,
        delayHours: step.delayHours || 0,
        delayMinutes: step.delayMinutes || 0,
      })),
    };

    if (sequenceData.id) {
      variables.id = sequenceData.id;
    }

    mutation({
      variables,
    })
      .then(({ data }) => {
        const result = sequenceData.id
          ? data.update_email_sequence
          : data.create_email_sequence;

        if (result.errors && result.errors.length > 0) {
          this.props.enqueueSnackbar(`Error: ${result.errors.join(', ')}`, {
            variant: 'error',
          });
        } else {
          this.props.enqueueSnackbar(
            sequenceData.id ? 'Sequence updated successfully' : 'Sequence created successfully',
            { variant: 'success' }
          );
          this.props.history.push('/admin/email_sequences');
        }
      })
      .catch(error => {
        this.props.enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
      });
  };

  handleCancel = () => {
    this.props.history.push('/admin/email_sequences');
  };

  render() {
    const sequenceId = this.props.match.params.id;
    const isEdit = !!sequenceId;

    return (
      <Query query={GET_TEMPLATES}>
        {({ loading: loadingTemplates, error: templatesError, data: templatesData }) => {
          if (loadingTemplates) {
            return (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
              </Box>
            );
          }

          if (templatesError) {
            return <div>Error loading templates: {templatesError.message}</div>;
          }

          const templates = templatesData.emailTemplates || [];

          if (isEdit) {
            return (
              <Query query={GET_SEQUENCE} variables={{ id: sequenceId }}>
                {({ loading: loadingSequence, error: sequenceError, data: sequenceData }) => {
                  if (loadingSequence) {
                    return (
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress />
                      </Box>
                    );
                  }

                  if (sequenceError) {
                    return <div>Error loading sequence: {sequenceError.message}</div>;
                  }

                  // Map steps to emailSequenceSteps for component compatibility
                  const sequenceWithSteps = {
                    ...sequenceData.emailSequence,
                    emailSequenceSteps: sequenceData.emailSequence.steps,
                  };

                  return (
                    <Mutation mutation={CREATE_SEQUENCE}>
                      {(createSequence) => (
                        <Mutation mutation={UPDATE_SEQUENCE}>
                          {(updateSequence) => (
                            <SequenceBuilder
                              sequence={sequenceWithSteps}
                              templates={templates}
                              onSave={this.handleSave(createSequence, updateSequence)}
                              onCancel={this.handleCancel}
                            />
                          )}
                        </Mutation>
                      )}
                    </Mutation>
                  );
                }}
              </Query>
            );
          }

          return (
            <Mutation mutation={CREATE_SEQUENCE}>
              {(createSequence) => (
                <Mutation mutation={UPDATE_SEQUENCE}>
                  {(updateSequence) => (
                    <SequenceBuilder
                      sequence={null}
                      templates={templates}
                      onSave={this.handleSave(createSequence, updateSequence)}
                      onCancel={this.handleCancel}
                    />
                  )}
                </Mutation>
              )}
            </Mutation>
          );
        }}
      </Query>
    );
  }
}

export default withRouter(withSnackbar(SequenceBuilderContainer));

